import {UserNs} from "@shared/types/user.namespace";

export namespace ProjectDocumentNs {

  export const MAX_FILE_SIZE = 25 * 1024 * 1024;             // 25 MB per file
  export const MAX_BATCH_FILES = 5;                          // per chat turn
  export const MAX_TOTAL_REQUEST_SIZE = 100 * 1024 * 1024;   // 100 MB total

  export const ALLOWED_MIME_TYPES: ReadonlyArray<string> = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'text/html',
  ];

  export const ALLOWED_EXTENSIONS: ReadonlyArray<string> = [
    '.pdf', '.docx', '.doc', '.pptx', '.xlsx', '.txt', '.md', '.html',
  ];

  // T6.3 (2026-10-10): a screen image attached to a chat turn is not a document — the backend
  // stores it and proposes the features visible on it. One image per message, 10 MB.
  export const IMAGE_MIME_TYPES: ReadonlyArray<string> = ['image/png', 'image/jpeg', 'image/webp'];
  export const IMAGE_EXTENSIONS: ReadonlyArray<string> = ['.png', '.jpg', '.jpeg', '.webp'];
  export const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
  export const MAX_IMAGES_PER_TURN = 1;

  export function isImageFile(file: File): boolean {
    if (file.type && IMAGE_MIME_TYPES.includes(file.type.toLowerCase())) return true;
    const lower = file.name.toLowerCase();
    return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext));
  }

  /** `accept=` attribute string for `<input type="file">`. */
  export const FILE_INPUT_ACCEPT =
    [...ALLOWED_EXTENSIONS, ...ALLOWED_MIME_TYPES, ...IMAGE_EXTENSIONS, ...IMAGE_MIME_TYPES].join(',');

  // ─── Content summary (LLM-derived, snake_case from backend JSON) ─

  export interface ContentSummaryItem {
    title: string;
    detail?: string;
    source?: string;
    [key: string]: unknown;
  }

  export interface ContentSummary {
    /** The project's summary, not this file's. */
    summary?: string;
    openPoints?: ContentSummaryItem[];
    projectNotes?: ContentSummaryItem[];
    capturedTopics?: ContentSummaryItem[];
    /** Attributed to this document. */
    decisions?: ContentSummaryItem[];
    entities?: ContentSummaryItem[];

    open_points?: ContentSummaryItem[];
    project_notes?: ContentSummaryItem[];
    captured_topics?: ContentSummaryItem[];

    [key: string]: unknown;
  }

  // ─── Analysis pipeline status (mirror backend pipeline-state enums) ─
  // `analysisStatus` is derived server-side from the newest analysis attempt of
  // the DOCUMENT_SUMMARY/schema-1 tuple; it is never persisted on the document.
  // `analysisErrorCode` is a bounded, non-sensitive allow-list value and is only
  // present while the latest attempt is ANALYSIS_FAILED. Missing status (older
  // backend during a rolling deploy) must be treated as CONVERTED by the client.

  export const ANALYSIS_STATUSES = [
    'CONVERTED',
    'ANALYSIS_PENDING',
    'ANALYZING',
    'ANALYZED',
    'ANALYSIS_FAILED',
    'SUPERSEDED',
  ] as const;

  export type AnalysisStatus = (typeof ANALYSIS_STATUSES)[number];

  export const ANALYSIS_ERROR_CODES = [
    'PROVIDER_UNAVAILABLE',
    'PROVIDER_ERROR',
    'INVALID_OUTPUT',
    'TIMEOUT',
    'INPUT_TOO_LARGE',
    'CANCELLED',
    'INTERNAL_ERROR',
  ] as const;

  export type AnalysisErrorCode = (typeof ANALYSIS_ERROR_CODES)[number];

  // ─── Context acceptance (held-document decision flow) ───────────
  // Whether the analysed content was added to the project context. Absent on
  // older backends → treat as NOT_ASSESSED. `pendingDecisionKey` is only set
  // while PENDING_DECISION and identifies the open decision in chat.

  export const CONTEXT_ACCEPTANCES = [
    'NOT_ASSESSED',
    'ACCEPTED',
    'PENDING_DECISION',
    'REJECTED',
  ] as const;

  export type ContextAcceptance = (typeof CONTEXT_ACCEPTANCES)[number];

  // ─── Document DTOs (mirror backend ProjectDocumentDto / DetailDto) ─

  /** Where a document came from. */
  export type DocumentOrigin = 'UPLOAD' | 'GOOGLE_DRIVE' | 'NOTION' | 'FIGMA';

  /** Where one arriving document came from. */
  export interface DocumentSource {
    origin: DocumentOrigin;
    /** A link back to it at the source. */
    url?: string;
    /** Its id at the source. */
    ref?: string;
  }

  export interface ProjectDocument {
    documentKey: string;
    /** The name a reader gave it. `null` means it has none and the file name is used. */
    title: string | null;
    fileName: string;
    fileType: string;
    fileSize: number;
    fileUrl: string | null;
    contentChars: number;
    hasContent: boolean;
    contentSummary: ContentSummary | null;
    /** Derived analysis state; absent/null on older backends → treat as CONVERTED. */
    analysisStatus?: AnalysisStatus | null;
    /** Safe error class; only set while `analysisStatus === 'ANALYSIS_FAILED'`. */
    analysisErrorCode?: AnalysisErrorCode | null;
    /** Held-document flow; absent on older backends → NOT_ASSESSED. */
    contextAcceptance?: ContextAcceptance | null;
    /** Only set while `contextAcceptance === 'PENDING_DECISION'`. */
    pendingDecisionKey?: string | null;
    origin: DocumentOrigin | null;
    /** A link back to the document at its source. For showing; may be absent. */
    originUrl: string | null;
    /** Its id at the source — Drive's `fileId`. */
    originRef: string | null;
    conversionEngine: string | null;
    conversionDurationMs: number | null;
    createDate: string;
    createdBy?: UserNs.AuditUser;
  }

  export interface ProjectDocumentDetail extends ProjectDocument {
    fileContent: string;
    updateDate: string | null;
  }

  // ─── Request / Response shapes ─────────────────────────────────

  export interface RenameRequest {
    workspaceId: string;
    projectId: string;
    documentKey: string;
    /** Empty clears the name; the document goes back to being called by its file. */
    title: string;
  }

  export interface ListRequest {
    workspaceId: string;
    projectId: string;
    before?: string | null;
    limit?: number | null;
  }

  export interface ListResponse {
    rows: ProjectDocument[];
    hasMore: boolean;
    before: string | null;
  }

  export interface DetailRequest {
    workspaceId: string;
    projectId: string;
    documentKey: string;
  }

  export interface DeleteRequest {
    workspaceId: string;
    projectId: string;
    documentKey: string;
  }

  export interface DeleteResponse {
    status: string;
  }

  /** Adding a source without a conversation — `POST project/document/upload`. */
  export interface UploadRequest {
    workspaceId: string;
    projectId: string;
    origin?: DocumentOrigin;
    originUrl?: string;
    originRef?: string;
  }

  export interface UploadResponse {
    /** The same rows `list` returns, in the order the files were given. */
    rows: ProjectDocument[];
  }
}
